import { extractQueryState } from "@shared/utils/extractQueryState";
import { useQuery } from "@tanstack/react-query";
import { getAuthApiClient } from "@shared/apis/sidekick/api-factories";
import { GetOAuth2ClientInfoResponse } from "@shared/apis/sidekick/generated";
import {
  ApiError,
  apiResponseErrorHandler,
} from "@shared/apis/sidekick/api-error-handler";
import { toast } from "@shared/services/toast";

type Props = {
  id: string | null;
  onSuccess?: (data: GetOAuth2ClientInfoResponse) => void;
  onError?: (error: ApiError) => void;
};

const queryKey = "oauth2-client";

export const useGetOAuth2ClientInfo = (params: Props) => {
  const { id = "", onSuccess, onError } = params;

  const api = getAuthApiClient();
  const query = useQuery({
    queryKey: [queryKey, id],
    queryFn: async () => {
      try {
        const response = await api.authControllerGetOAuth2ClientInfo({
          id: id || "",
        });
        onSuccess?.(response);
        return response;
      } catch (e) {
        console.error(`Failed to get client with id ${id}.`, e);
        const error = await apiResponseErrorHandler(e);
        onError?.(error);
        if (error.status === 404) {
          toast.error("Invalid client id.");
        }
        throw error;
      }
    },
    enabled: !!id,
    retry: false,
    refetchInterval: false,
    refetchOnWindowFocus: false,
  });

  return extractQueryState(query);
};
